/**
 * G7. Родительство: «Разговор». Раз в три года, пока младшему ребёнку
 * 6–18 (`flags.youngestAge`): ребёнок приходит с проблемой (школа, друзья,
 * деньги), три ответа — «услышит» / нейтральный / холодный, в перемешанном
 * порядке. При отношениях ≥ TALK_HINT_RELATIONS ответ «он услышит»
 * подсвечивается (`state.hint`). Услышал — отношения +3 и `flags.childBond`
 * +1; холодный — −3 и −1; нейтральный — ничего. `childBond` — скрытая
 * связь, которая позже решит, останется ли ребёнок рядом в старости
 * (забота от NPC-ребёнка, план 3+; движок пока флаг не читает).
 * «Пропустить» — нейтральный ответ.
 */
import type { Dataset, GamblingLaw } from '@life/data';
import type { Life, MinigameOutcome, Rng } from '@life/engine';
import type { MinigameDef } from '../types';
import { shuffle } from '../skill/common';
import { nothing, numberFlag, youngestAgeOf } from './common';

export const FLAG_CHILD_BOND = 'childBond';
export const TALK_MIN_CHILD_AGE = 6;
export const TALK_MAX_CHILD_AGE = 18;
export const TALK_EVERY_YEARS = 3;
export const TALK_PROBLEMS = ['school', 'friends', 'money'] as const;
export type TalkProblem = (typeof TALK_PROBLEMS)[number];
export const TALK_ANSWERS = 3;
export const TALK_HINT_RELATIONS = 50;
export const TALK_RELATIONS = 3;
/** Возраст игрока для триггера по возрасту; доступность решает возраст ребёнка. */
export const TALK_MIN_AGE = 18;
export const TALK_MAX_AGE = 100;
/** Индексы ответов «услышит» и «холодный» в банке (до перемешивания): 0 — услышит, 1 — нейтральный, 2 — холодный. */
export const TALK_HEARD_INDEX = 0;
export const TALK_COLD_INDEX = 2;

export interface TalkState {
  problem: TalkProblem;
  /** Ключи ответов в показанном порядке: `minigame.talk.<problem>.<i>`. */
  answers: string[];
  /** Индекс ответа «услышит» в `answers`. */
  heard: number;
  /** Индекс холодного ответа в `answers`. */
  cold: number;
  /** Подсвеченный ответ (= heard) при достаточных отношениях, иначе null. */
  hint: number | null;
}

export type TalkInput = { pick: number };

export function talkProblemKey(problem: string): string {
  return `minigame.talk.${problem}`;
}

export function talkAnswerKey(problem: string, i: number): string {
  return `minigame.talk.${problem}.${i}`;
}

/** Подходит ли возраст младшего ребёнка: 6, 9, 12, 15, 18. */
export function talkDue(childAge: number | null): boolean {
  return childAge !== null && childAge >= TALK_MIN_CHILD_AGE && childAge <= TALK_MAX_CHILD_AGE && (childAge - TALK_MIN_CHILD_AGE) % TALK_EVERY_YEARS === 0;
}

function result(life: Life, kind: 'heard' | 'neutral' | 'cold'): MinigameOutcome {
  const bond = numberFlag(life, FLAG_CHILD_BOND);
  const delta = kind === 'heard' ? 1 : kind === 'cold' ? -1 : 0;
  return {
    ...nothing(),
    stats: { relations: delta * TALK_RELATIONS },
    flags: { [FLAG_CHILD_BOND]: bond + delta },
    logKey: `minigame.talk.${kind}`,
  };
}

export const talk: MinigameDef<TalkInput, TalkState> = {
  id: 'talk',
  section: 'G',
  triggers: [{ kind: 'age', min: TALK_MIN_AGE, max: TALK_MAX_AGE, everyYears: 1 }],
  available(life: Life, _ds: Dataset, _law: GamblingLaw): boolean {
    return life.children > 0 && talkDue(youngestAgeOf(life));
  },
  setup(life: Life, _ds: Dataset, rng: Rng): TalkState {
    const problem = rng.pick(TALK_PROBLEMS);
    const order = shuffle([0, 1, 2], rng);
    const answers = order.map((i) => talkAnswerKey(problem, i));
    const heard = order.indexOf(TALK_HEARD_INDEX);
    const cold = order.indexOf(TALK_COLD_INDEX);
    return { problem, answers, heard, cold, hint: life.stats.relations >= TALK_HINT_RELATIONS ? heard : null };
  },
  play(state: TalkState, input: TalkInput, life: Life): MinigameOutcome {
    if (input.pick === state.heard) return result(life, 'heard');
    if (input.pick === state.cold) return result(life, 'cold');
    return result(life, 'neutral');
  },
  expected(_state: TalkState, life: Life): MinigameOutcome {
    return result(life, 'neutral');
  },
};
